/**
 * Widget manager for the Shipment Overview strip.
 *
 * A popup off the strip's own icon, laid out like the table's Views control: the
 * same list of rows, each with its name and its inline actions, over a bordered
 * footer of add/reset actions, with the tab bar control's drag handle for the rows
 * that can be ordered. Every action commits as it is made — as in those controls
 * there is no draft to apply, so a row's name, its position and whether it is on
 * the strip at all are written the moment they change.
 *
 * Every row is the user's to revise — the per-status ones included: rename it,
 * drag it, or open its filter and build on the status it started from.
 */

import React, { useEffect, useRef, useState } from "react";
import {
  Check,
  GripHorizontal,
  Loader2,
  Plus,
  RotateCcw,
  SlidersHorizontal,
  X,
} from "lucide-react";
import ConfirmDeleteModal from "../../../../components/ACS-Dashboard/ACS-Blocks/ConfirmDeleteModal";
import {
  AdvancedFilterModal,
  DEFAULT_ADVANCED_FILTERS,
} from "../TabContent/shipmentShared";
import { toViewStatus } from "../TabContent/Utils/shipmentStatuses";
import {
  AGGREGATION_FIELDS,
  AGGREGATION_OPTIONS,
  comparePresetForMinutes,
  COMPARE_PRESETS,
  describeWidgetFilters,
  hasAdvancedFilters,
  useSavedTabSuggestions,
  WIDGET_NAME_MAX_LENGTH,
  widgetKey,
  withFilters,
} from "./overviewWidgets";

const CUSTOM_COMPARE_VALUE = "custom";

/** The <select>'s current value for a widget's stored comparePeriodMinutes. */
const compareSelectValue = (minutes) => {
  if (!minutes) return "";
  return comparePresetForMinutes(minutes) ? String(minutes) : CUSTOM_COMPARE_VALUE;
};

/** A widget stands in for a whole status while it narrows by nothing else. */
const coversStatus = (widget) => Boolean(widget.status) && !hasAdvancedFilters(widget);

/**
 * A widget still exactly as the strip generated it — a status under its own label.
 * Anything else carries work the user did (a name, a filter), so it is not
 * something to drop on a single click.
 */
const isUntouchedStatusWidget = (widget) =>
  coversStatus(widget) && widget.name === toViewStatus(widget.status);

/** What a save needs from a widget — the rest is the record's own business. */
const toSaveShape = (widget) => ({
  id: widget.id ?? null,
  name: widget.name,
  status: widget.status ?? null,
  advancedFilters: widget.advancedFilters,
  comparePeriodMinutes: widget.comparePeriodMinutes ?? null,
  aggregation: widget.aggregation || "COUNT",
  aggregationField: widget.aggregationField ?? null,
});

const OverviewWidgetsModal = ({
  isOpen = false,
  widgets = [],
  apiStatuses = [],
  loading = false,
  error = null,
  onSave,
  onReset,
  onClose,
}) => {
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [renamingKey, setRenamingKey] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  // The widget whose filter is being edited, or "new" while one is being added.
  const [filterTarget, setFilterTarget] = useState(null);
  // Filters chosen for a widget that still needs a name.
  const [pendingFilters, setPendingFilters] = useState(null);
  const [newWidgetName, setNewWidgetName] = useState("");
  const [pendingDeleteWidget, setPendingDeleteWidget] = useState(null);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [dragOverKey, setDragOverKey] = useState(null);
  const dragSourceKey = useRef(null);
  // The widget whose custom-minutes input is open, plus its in-progress value —
  // only one at a time, same as renamingKey.
  const [customCompareKey, setCustomCompareKey] = useState(null);
  const [customCompareValue, setCustomCompareValue] = useState("");
  // Only fetched once the popup is opened — it is a convenience, not part of load.
  const savedTabSuggestions = useSavedTabSuggestions({ enabled: isOpen });

  // Every open starts clean: no half-typed name, no row left mid-rename.
  useEffect(() => {
    if (isOpen) return;
    setRenamingKey(null);
    setRenameValue("");
    setPendingDeleteWidget(null);
    setPendingFilters(null);
    setNewWidgetName("");
    setFilterTarget(null);
    setLocalError(null);
    setCustomCompareKey(null);
    setCustomCompareValue("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      // Only the topmost layer should answer Escape.
      const nestedLayerOpen = filterTarget || pendingDeleteWidget || confirmingReset;
      if (e.key === "Escape" && !busy && !nestedLayerOpen) onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, busy, filterTarget, pendingDeleteWidget, confirmingReset, onClose]);

  const run = async (fn) => {
    setBusy(true);
    setLocalError(null);
    try {
      await fn();
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Something went wrong";
      setLocalError(message);
    } finally {
      setBusy(false);
    }
  };

  /** Persist the strip as `next` — every action here is one of these. */
  const save = (next) => onSave?.(next.map(toSaveShape));

  // Statuses with no widget of their own, offered underneath so they can be shown
  // again — the widget equivalent of the Views list's "System default" row.
  const covered = new Set(widgets.filter(coversStatus).map((w) => w.status));
  const hiddenStatuses = apiStatuses.filter((status) => !covered.has(status));

  // Same rule the tab bar uses for pinnable views: anything already on the strip
  // under that name drops out, so the list reads as what is still worth adding.
  const widgetNames = new Set(
    widgets.map((w) => String(w.name || "").trim().toLowerCase())
  );
  const suggestedTabs = savedTabSuggestions.filter(
    (tab) => !widgetNames.has(tab.name.trim().toLowerCase())
  );

  const addFromSavedTab = (tab) =>
    run(() =>
      save([
        ...widgets,
        {
          id: null,
          name: tab.name,
          status: tab.status,
          advancedFilters: tab.advancedFilters,
        },
      ])
    );

  const showStatus = (status) =>
    run(() =>
      save([
        ...widgets,
        {
          id: null,
          name: toViewStatus(status),
          status,
          advancedFilters: DEFAULT_ADVANCED_FILTERS,
        },
      ])
    );

  const removeWidget = (widget) =>
    save(widgets.filter((w) => widgetKey(w) !== widgetKey(widget)));

  const setComparePeriod = (widget, minutes) =>
    run(() =>
      save(
        widgets.map((w) =>
          widgetKey(w) === widgetKey(widget) ? { ...w, comparePeriodMinutes: minutes } : w
        )
      )
    );

  const setAggregation = (widget, aggregation) =>
    run(() =>
      save(
        widgets.map((w) => {
          if (widgetKey(w) !== widgetKey(widget)) return w;
          // Switching back to Count drops whatever field was chosen — it's meaningless
          // there, and leaving it set would silently resurface if the user switched to
          // Sum/Average again expecting a fresh choice.
          const aggregationField =
            aggregation === "COUNT" ? null : w.aggregationField || AGGREGATION_FIELDS[0].key;
          return { ...w, aggregation, aggregationField };
        })
      )
    );

  const setAggregationField = (widget, aggregationField) =>
    run(() =>
      save(
        widgets.map((w) =>
          widgetKey(w) === widgetKey(widget) ? { ...w, aggregationField } : w
        )
      )
    );

  const handleCompareSelect = (widget, value) => {
    if (value === CUSTOM_COMPARE_VALUE) {
      setCustomCompareKey(widgetKey(widget));
      setCustomCompareValue(
        widget.comparePeriodMinutes ? String(widget.comparePeriodMinutes) : ""
      );
      return;
    }
    setCustomCompareKey(null);
    setComparePeriod(widget, value ? Number(value) : null);
  };

  const commitCustomCompare = (widget) => {
    const minutes = Math.trunc(Number(customCompareValue));
    setCustomCompareKey(null);
    if (!Number.isFinite(minutes) || minutes <= 0) return;
    setComparePeriod(widget, minutes);
  };

  const startRename = (widget, e) => {
    e?.stopPropagation?.();
    setRenamingKey(widgetKey(widget));
    setRenameValue(widget.name);
  };

  const cancelRename = () => {
    setRenamingKey(null);
    setRenameValue("");
  };

  const commitRename = (widget) =>
    run(async () => {
      const trimmed = renameValue.trim();
      if (!trimmed) return;
      await save(
        widgets.map((w) =>
          widgetKey(w) === widgetKey(widget) ? { ...w, name: trimmed } : w
        )
      );
      cancelRename();
    });

  const applyFilters = (filters) =>
    run(async () => {
      if (filterTarget === "new") {
        // A new widget needs a name before it can be saved; the filter is held
        // until the name input below is committed.
        setPendingFilters(filters);
        setFilterTarget(null);
        return;
      }
      const target = filterTarget;
      setFilterTarget(null);
      await save(
        widgets.map((w) => (widgetKey(w) === widgetKey(target) ? withFilters(w, filters) : w))
      );
    });

  const commitNewWidget = () =>
    run(async () => {
      const trimmed = newWidgetName.trim();
      if (!trimmed || !pendingFilters) return;
      await save([
        ...widgets,
        { id: null, name: trimmed, status: null, advancedFilters: pendingFilters },
      ]);
      setPendingFilters(null);
      setNewWidgetName("");
    });

  const confirmDelete = () =>
    run(async () => {
      await removeWidget(pendingDeleteWidget);
      setPendingDeleteWidget(null);
    });

  const confirmReset = () =>
    run(async () => {
      await onReset?.();
      setConfirmingReset(false);
      onClose?.();
    });

  // Move-to-index rather than a swap, matching how the tab bar reorders: dragging a
  // row across several others shifts them all by one.
  const handleDrop = (targetWidget) => {
    const sourceKey = dragSourceKey.current;
    dragSourceKey.current = null;
    setDragOverKey(null);

    const targetKey = widgetKey(targetWidget);
    if (!sourceKey || sourceKey === targetKey) return;

    const sourceIndex = widgets.findIndex((w) => widgetKey(w) === sourceKey);
    const targetIndex = widgets.findIndex((w) => widgetKey(w) === targetKey);
    if (sourceIndex < 0 || targetIndex < 0) return;

    const next = [...widgets];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    run(() => save(next));
  };

  const displayError = localError || error;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40 p-4"
      // Clicking off the panel closes it, the way clicking off the Views dropdown does.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose?.();
      }}
    >
      <div className="flex max-h-[85vh] w-full min-w-[320px] max-w-2xl flex-col rounded-md border border-gray-200 bg-white shadow-lg dark:border-acs-dark-border dark:bg-acs-dark-surface">
        {/* A popup needs a title bar where the dropdown had its trigger. */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
          <div className="flex items-center gap-2">
            {(loading || busy) && (
              <Loader2 size={14} className="shrink-0 animate-spin text-gray-500" />
            )}
            <h3 className="text-sm font-semibold text-gray-900 dark:text-acs-dark-text">
              Widgets{widgets.length ? ` (${widgets.length})` : ""}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-acs-dark-hover"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        {displayError && (
            <p className="border-b border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
              {displayError}
            </p>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-1">
            {widgets.length === 0 && !loading && (
              <p className="px-4 py-2 text-xs text-gray-500 dark:text-acs-dark-subtext">
                No widgets on the strip.
              </p>
            )}

            {widgets.map((widget) => {
              const key = widgetKey(widget);
              const isRenaming = renamingKey === key;
              // Untouched status widgets are the only ones that can be dropped on a
              // single click — everything else carries a name or filter the user set.
              const isUntouched = isUntouchedStatusWidget(widget);
              const isDragOver = dragOverKey === key;

              return (
                <div
                  key={key}
                  title={describeWidgetFilters(widget)}
                  // Not draggable mid-rename: the row would steal the pointer from
                  // the text input.
                  draggable={!isRenaming && !busy}
                  onDragStart={() => {
                    dragSourceKey.current = key;
                  }}
                  onDragOver={(e) => {
                    if (dragSourceKey.current == null) return;
                    e.preventDefault();
                    setDragOverKey(key);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleDrop(widget);
                  }}
                  onDragEnd={() => {
                    dragSourceKey.current = null;
                    setDragOverKey(null);
                  }}
                  className={`group px-2 py-1 ${isRenaming ? "" : "cursor-grab select-none"} ${
                    isDragOver
                      ? "rounded-md border border-[#223688]/40 bg-[#223688]/10 dark:border-[#5d6fc0]/50 dark:bg-[#223688]/25"
                      : "border border-transparent hover:bg-gray-50 dark:hover:bg-acs-dark-hover"
                  }`}
                >
                  {isRenaming ? (
                    <div
                      className="flex items-center gap-1 px-1 py-0.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        placeholder="Widget name"
                        maxLength={WIDGET_NAME_MAX_LENGTH}
                        autoFocus
                        className="min-w-0 flex-1 rounded border px-2 py-1 text-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitRename(widget);
                          if (e.key === "Escape") cancelRename();
                        }}
                      />
                      <button
                        type="button"
                        title="Save name"
                        disabled={busy || !renameValue.trim()}
                        className="rounded p-1 text-[#223688] hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-acs-dark-hover"
                        onClick={() => commitRename(widget)}
                      >
                        <Check size={14} />
                      </button>
                      <button
                        type="button"
                        title="Cancel edit"
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
                        onClick={cancelRename}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex min-w-0 items-center gap-1 px-1 py-1.5">
                      <GripHorizontal
                        size={13}
                        className="mr-1.5 shrink-0 text-gray-300 transition-colors group-hover:text-gray-400 dark:text-gray-600"
                      />
                      {/* Every widget is the user's to revise, the status ones
                          included: rename it, or open its filter and build on the
                          status it started from. */}
                      <button
                        type="button"
                        title="Click to rename"
                        disabled={busy}
                        className="min-w-0 flex-1 truncate text-left text-sm hover:underline"
                        onClick={(e) => startRename(widget, e)}
                      >
                        {widget.name}
                      </button>
                      {!isUntouched && (
                        <span className="min-w-0 max-w-[50%] shrink truncate text-xs text-gray-400 dark:text-acs-dark-subtext">
                          {describeWidgetFilters(widget)}
                        </span>
                      )}

                      {/* Aggregation — Count by default (unchanged behaviour); Sum/Average
                          need a numeric field, since "count of Total Charges" isn't a
                          meaningful number the way "count of shipments" is. */}
                      <select
                        value={widget.aggregation || "COUNT"}
                        disabled={busy}
                        title="Aggregation"
                        className="shrink-0 rounded border bg-white px-1 py-0.5 text-xs text-gray-600 dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-subtext"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setAggregation(widget, e.target.value)}
                      >
                        {AGGREGATION_OPTIONS.map((a) => (
                          <option key={a.key} value={a.key}>{a.label}</option>
                        ))}
                      </select>

                      {widget.aggregation && widget.aggregation !== "COUNT" && (
                        <select
                          value={widget.aggregationField || AGGREGATION_FIELDS[0].key}
                          disabled={busy}
                          title="Field to aggregate"
                          className="shrink-0 rounded border bg-white px-1 py-0.5 text-xs text-gray-600 dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-subtext"
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setAggregationField(widget, e.target.value)}
                        >
                          {AGGREGATION_FIELDS.map((f) => (
                            <option key={f.key} value={f.key}>{f.label}</option>
                          ))}
                        </select>
                      )}

                      {/* "Compare with" — an opt-in rolling-window trend chip, off by
                          default. Only ever ANDs an extra createdDate bound onto the
                          widget's own filter; it never changes what the widget counts. */}
                      {customCompareKey === key ? (
                        <input
                          type="number"
                          min="1"
                          autoFocus
                          value={customCompareValue}
                          onChange={(e) => setCustomCompareValue(e.target.value)}
                          onBlur={() => commitCustomCompare(widget)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitCustomCompare(widget);
                            if (e.key === "Escape") setCustomCompareKey(null);
                          }}
                          placeholder="Minutes"
                          title="Compare with: custom minutes ago"
                          className="w-20 shrink-0 rounded border px-1.5 py-0.5 text-xs dark:border-acs-dark-border dark:bg-acs-dark-surface"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <select
                          value={compareSelectValue(widget.comparePeriodMinutes)}
                          disabled={busy}
                          title="Compare with"
                          className="shrink-0 rounded border bg-white px-1 py-0.5 text-xs text-gray-600 dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-subtext"
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleCompareSelect(widget, e.target.value)}
                        >
                          {COMPARE_PRESETS.map((p) => (
                            <option key={p.label} value={p.minutes ?? ""}>
                              {p.label}
                            </option>
                          ))}
                          <option value={CUSTOM_COMPARE_VALUE}>Custom…</option>
                        </select>
                      )}

                      <button
                        type="button"
                        title="Edit filter"
                        disabled={busy}
                        className="ml-auto shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-[#223688] dark:hover:bg-acs-dark-hover"
                        onClick={() => setFilterTarget(widget)}
                      >
                        <SlidersHorizontal size={14} />
                      </button>
                      <button
                        type="button"
                        // An untouched status widget is one click away in the list
                        // below, so it goes without a confirm; a revised one carries
                        // work that would be gone for good.
                        title={isUntouched ? "Remove from the strip" : "Delete widget"}
                        disabled={busy}
                        className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                        onClick={() =>
                          isUntouched
                            ? run(() => removeWidget(widget))
                            : setPendingDeleteWidget(widget)
                        }
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {hiddenStatuses.map((status) => (
              <div
                key={`hidden:${status}`}
                className="border border-transparent px-2 py-1 hover:bg-gray-50 dark:hover:bg-acs-dark-hover"
              >
                <div className="flex min-w-0 items-center gap-1 px-1 py-1.5">
                  {/* Nothing to drag until it is on the strip — keeps the names
                      lined up with the rows above. */}
                  <span className="mr-1.5 w-[13px] shrink-0" />
                  <button
                    type="button"
                    disabled={busy}
                    className="min-w-0 flex-1 truncate text-left text-sm text-gray-500 dark:text-acs-dark-subtext"
                    onClick={() => showStatus(status)}
                  >
                    {toViewStatus(status)}
                  </button>
                  <button
                    type="button"
                    title="Add to the strip"
                    disabled={busy}
                    className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-[#223688] dark:hover:bg-acs-dark-hover"
                    onClick={() => showStatus(status)}
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="max-h-[40vh] shrink-0 overflow-y-auto border-t border-gray-200 p-2 dark:border-acs-dark-border">
            {pendingFilters ? (
              <div className="flex flex-col gap-2">
                <p className="px-1 text-xs text-gray-500 dark:text-acs-dark-subtext">
                  Counting: {describeWidgetFilters({ advancedFilters: pendingFilters })}
                </p>
                <input
                  value={newWidgetName}
                  onChange={(e) => setNewWidgetName(e.target.value)}
                  placeholder="Widget name"
                  maxLength={WIDGET_NAME_MAX_LENGTH}
                  autoFocus
                  className="w-full rounded border px-2 py-1 text-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
                  onKeyDown={(e) => e.key === "Enter" && commitNewWidget()}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="flex-1 rounded bg-[#223688] px-2 py-1 text-xs font-semibold text-white disabled:opacity-50"
                    disabled={busy || !newWidgetName.trim()}
                    onClick={commitNewWidget}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="rounded px-2 py-1 text-xs text-gray-600 dark:text-acs-dark-subtext"
                    onClick={() => {
                      setPendingFilters(null);
                      setNewWidgetName("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  disabled={busy}
                  className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-acs-dark-hover"
                  onClick={() => setFilterTarget("new")}
                >
                  <Plus size={14} /> Add filter widget
                </button>
                <button
                  type="button"
                  disabled={busy}
                  className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-950/30"
                  onClick={() => setConfirmingReset(true)}
                >
                  <RotateCcw size={14} /> Reset to defaults
                </button>

                {suggestedTabs.length > 0 && (
                  <div className="mt-1 border-t border-gray-200 pt-1 dark:border-acs-dark-border">
                    <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-acs-dark-subtext">
                      Add widget from saved tab
                    </p>
                    {suggestedTabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        disabled={busy}
                        title={describeWidgetFilters(tab)}
                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-acs-dark-hover"
                        onClick={() => addFromSavedTab(tab)}
                      >
                        <Plus size={14} className="shrink-0" />
                        <span className="min-w-0 flex-1 truncate">{tab.name}</span>
                        <span className="min-w-0 max-w-[50%] shrink truncate text-xs text-gray-400 dark:text-acs-dark-subtext">
                          {describeWidgetFilters(tab)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      {/* Nested layers sit inside the overlay so a mousedown in one of them is never
          mistaken for a click on the backdrop, which would close the popup under it. */}
      {filterTarget && (
        <AdvancedFilterModal
          filters={
            filterTarget === "new"
              ? DEFAULT_ADVANCED_FILTERS
              : filterTarget.advancedFilters
          }
          quickFilterStatus={filterTarget === "new" ? null : filterTarget.status}
          onApply={applyFilters}
          onClose={() => setFilterTarget(null)}
        />
      )}

      <ConfirmDeleteModal
        isOpen={pendingDeleteWidget != null}
        title="Delete widget"
        headline={`Delete the “${pendingDeleteWidget?.name ?? ""}” widget?`}
        description="This cannot be undone. The shipments it counted are untouched — only the widget is deleted."
        confirmLabel="Delete widget"
        busy={busy}
        error={localError}
        overlayClassName="z-[6000]"
        onCancel={() => setPendingDeleteWidget(null)}
        onConfirm={confirmDelete}
      />

      <ConfirmDeleteModal
        isOpen={confirmingReset}
        title="Reset widgets"
        headline="Reset the overview widgets to the default set?"
        description="This cannot be undone. Your custom widgets and their order are deleted, and the strip goes back to one widget per status."
        confirmLabel="Reset"
        busy={busy}
        error={localError}
        overlayClassName="z-[6000]"
        onCancel={() => setConfirmingReset(false)}
        onConfirm={confirmReset}
      />
    </div>
  );
};

export default OverviewWidgetsModal;
